import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ErrorHandler } from '../../../../globals/classes/errorHandler';
import { finalize } from 'rxjs/operators/finalize';
import { UserAuth } from '../../../classes/userAuth';
import {
	ISignInRequest,
	IUserIDRequest,
	IUserStatusResponse,
} from '../../../classes/http-requests';
import {
	IImpersonationUserStatusResponse,
	ITwoFactorAuthenticationInfo,
	TwoFactorAuthenticationInfo,
} from '../../../classes/http-responses';
import { Extensions } from '../../../classes/static/extensions';
import { RSAKey } from '../../../../globals/classes/rsa/rsaKey';
import { AppSettings } from '../../../app.settings';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PasswordRetrievalComponent } from '../../password-retrieval/password-retrieval.component';
import {
	IFetchUnlockEmailResponse,
	UnlockAcountEmail,
} from '../../../classes/unlock-account-email';
import { UserData, IUserData } from '../../../../globals/classes/userData';
import { IErrorDescription } from '@ibank-globals/error';
import { GoogleAnalyticsUtils } from '../../../classes/static/google-analytics-utils';
import { RegisterUserModalComponent } from '../../register-user-modal/register-user-modal.component';
import { RestoreWithAuthenticationComponent } from '../../password-retrieval/restore-with-authentication/restore-with-authentication.component';
import { CreatePasswordComponent } from '../../create-password/create-password.component';
import { Constants } from '../../../classes/static/constants';
import { AutoRefreshTimerService } from '../../../services/auto-refresh-timer.service';
import { UsernameRecoveryModalComponent } from '../../username-recovery-modal/username-recovery-modal.component';
import { ConnectProfileModalComponent } from '../../connect-profile-modal/connect-profile-modal.component';
import { LinkUsersService } from '../../../services/link-user.service';
import { LoginAction } from '../../../classes/static/enums';
import { TwoFAModalComponent } from '../../two-fa-modal/two-fa-modal.component';
import { ImpersonationService } from '../../../services/impersonation.service';
import { CardModalComponent } from '../../card-modal/card-modal.component';
import { BusinessRegistrationInfoModalComponent } from '../../business-registration-info-modal.component/business-registration-info-modal.component';

@Component({
	selector: 'sign-in-form',
	template: require('./sign-in-form.component.html'),
})
export class SignInFormComponent implements OnInit, AfterViewInit {
	@Input() public action: LoginAction;

	public username: string;
	public password: string;
	private rememberLogin = false;
	private request: ISignInRequest;

	public isCorporateUser: boolean = false;
	public loginStep = 1;
	public isPasswordVisible: boolean = false;
	public showLoginForm = false;
	public isLoading = false;
	public isForgotPasswordLoading = false;
	private userData: IUserData = new UserData();
	public isBlocked: boolean;
	public uiShowBlockedCorpUser: boolean = false;
	public uiShowForgotCorpUser: boolean = false;

	public isCustomerOnboardingEnabled: boolean = AppSettings.isCustOnboardingEnabled;

	@ViewChild('usernameRef', { static: false }) public usernameRef: ElementRef;
	@ViewChild('passwordRef', { static: false }) public passwordRef: ElementRef;

	constructor(
		private elRef: ElementRef,
		private route: ActivatedRoute,
		private modalService: NgbModal
	) {}

	public ngOnInit() {
		UserData.isCorporate$.subscribe((isCorporate) => (this.isCorporateUser = isCorporate));
		this.initializeSignInRequest();
		this.route.queryParams.subscribe((params) => {
			this.request.returnUrl = Extensions.getReturnUrl(params)
				? Extensions.getReturnUrl(params)
				: Extensions.returnUrl;
			Extensions.returnUrl = this.request.returnUrl;

			if (params['username']) {
				this.username = params['username'];
			}
		});
	}

	public ngAfterViewInit(): void {
		this.setUpKeyListener();
		this.focusUsernameField();
	}

	public toggleForgetPassword() {
		this.modalService.open(UsernameRecoveryModalComponent, {
			windowClass: 'modal-small',
		});
	}
	public toggleCardModal(): void {
		this.modalService.open(CardModalComponent, {
			windowClass: 'modal-small',
		});
	}

	public toggleBusinessRegistrationInfoModal() {
		this.modalService.open(BusinessRegistrationInfoModalComponent, {
			windowClass: 'modal-small',
		});
	}

	private setUpKeyListener(): void {
		this.elRef.nativeElement
			.querySelector('.user-login-subcontainer')
			.addEventListener('keydown', (e: any) => {
				let key = e.which || e.keyCode;
				if (key === Constants.KEY_ENTER) {
					if (this.loginStep == 1) {
						this.next();
						this.preventEventPropagation(e);
					} else if (this.loginStep == 2) {
						this.performLogin();
						this.preventEventPropagation(e);
					}
				}
			});
	}

	private preventEventPropagation(e: any) {
		e.stopPropagation();
		e.preventDefault();
	}

	private focusUsernameField() {
		setTimeout(() => {
			!!this.usernameRef &&
				!!this.usernameRef.nativeElement &&
				this.usernameRef.nativeElement.focus();
		}, 500);
	}

	private focusPasswordField(): void {
		setTimeout(() => {
			!!this.passwordRef &&
				!!this.passwordRef.nativeElement &&
				this.passwordRef.nativeElement.focus();
		}, 500);
	}

	public toggleVisibility() {
		this.isPasswordVisible = !this.isPasswordVisible;
	}

	public toggleRegister() {
		this.modalService.open(RegisterUserModalComponent, {
			windowClass: 'modal-small',
		});
	}

	public next() {
		GoogleAnalyticsUtils.sendContinueEvent();
		if (this.action === LoginAction.LinkUsers) {
			this.callLinkUserStatusService();
		} else if (this.action === LoginAction.ImpersonationLogin) {
			this.callImpersonationUserStatusService();
		} else this.callUserStatusService();
	}

	public back() {
		this.variablesAtUsernameInput();
		this.focusUsernameField();
	}

	private variablesAtUsernameInput() {
		this.username = null;
		this.password = null;

		this.isCorporateUser = false;
		this.isBlocked = false;
		this.uiShowBlockedCorpUser = false;
		this.uiShowForgotCorpUser = false;
		this.loginStep = 1;
	}

	public performLogin() {
		if (!this.hasValidFields()) return;
		this.isLoading = true;
		this.request.username = this.username;
		this.request.rememberLogin = this.rememberLogin;

		if (AppSettings.enforcePasswordEncryption) {
			const rsaKey = new RSAKey();
			rsaKey.setPublic(AppSettings.getPropertyN(), AppSettings.getPropertyE());
			this.request.password = rsaKey.encrypt(this.password);
		} else {
			this.request.password = this.password;
		}

		GoogleAnalyticsUtils.sendLoginEvent();
		AutoRefreshTimerService.lock();
		this.action === LoginAction.LinkUsers
			? this.callLinkUserService()
			: this.callSignInService();
	}

	private callImpersonationUserStatusService(): void {
		this.isLoading = true;
		ImpersonationService.checkUserStatus(this.username)
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe(
				(result) => this.handleStatusResponse(result),
				(error) => this.handleError(error)
			);
	}

	private handleStatusResponse(result: IImpersonationUserStatusResponse): void {
		this.isLoading = false;

		if (!!result && !!result.additionalAuthenticationInfo) {
			this.goToCodeVerification(result.additionalAuthenticationInfo);
		}
	}

	private callUserStatusService(): void {
		this.isLoading = true;
		UserAuth.checkUserStatus(this.username)
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe(
				(result) => this.handleUserStatusResponse(result),
				(error) => this.handleError(error)
			);
	}

	private callLinkUserStatusService(): void {
		this.isLoading = true;
		LinkUsersService.checkUserStatus(this.username)
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe(
				(result) => this.handleUserStatusResponse(result),
				(error) => this.handleError(error)
			);
	}

	private handleUserStatusResponse(
		result: IUserStatusResponse | IImpersonationUserStatusResponse
	): void {
		this.isBlocked = result.userStatus === 'BLOCKED';
		if (this.isBlocked) {
			this.retrievePassword();
			return;
		}
		if (result.userStatus === 'NEW' || result.userStatus === 'INIT') {
			this.createNewUser(result.userStatus);
		} else {
			if (this.action === LoginAction.ImpersonationLogin) {
				const modalRef = this.modalService.open(TwoFAModalComponent, {
					windowClass: 'modal-small',
				});
				(modalRef.componentInstance as TwoFAModalComponent).twoFAInfo =
					new TwoFactorAuthenticationInfo(
						(result as IImpersonationUserStatusResponse).additionalAuthenticationInfo
					);
			} else {
				this.loginStep = 2;
				this.focusPasswordField();
			}
		}
	}

	private callSignInService(): void {
		UserAuth.signIn(this.request)
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe(
				(result) => this.handleSignInResponse(result),
				(error) => {
					AutoRefreshTimerService.unlock();
					if (error.desc === 'password-blocked') {
						this.isBlocked = true;
						this.retrievePassword();
					} else {
						this.handleError(error);
					}
				}
			);
	}

	private callLinkUserService(): void {
		LinkUsersService.initiateUserLink(this.request)
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe(
				(result) => this.handleLinkUserResponse(result),
				(error) => {
					AutoRefreshTimerService.unlock();
					if (error.desc === 'password-blocked') {
						this.isBlocked = true;
						this.retrievePassword();
						return;
					}
					this.handleError(error);
				}
			);
	}

	private handleError(error: IErrorDescription): void {
		this.isLoading = false;
		ErrorHandler.newError(error);
	}

	private handleLinkUserResponse(response: any): void {
		this.modalService.open(ConnectProfileModalComponent, {
			windowClass: 'modal-small',
		});
		this.variablesAtUsernameInput();
	}

	private handleSignInResponse(response: ITwoFactorAuthenticationInfo) {
		this.isLoading = false;

		if (!!response) {
			this.goToCodeVerification(response);
		}
	}

	private goToCodeVerification(twofaInfo: ITwoFactorAuthenticationInfo): void {
		const modalRef = this.modalService.open(TwoFAModalComponent, {
			windowClass: 'modal-small',
		});
		(modalRef.componentInstance as TwoFAModalComponent).twoFAInfo =
			new TwoFactorAuthenticationInfo(twofaInfo);
	}

	public retrievePassword() {
		if (this.isCorporateUser && this.isBlocked) {
			this.uiShowBlockedCorpUser = true;
			this.isForgotPasswordLoading = false;
			return;
		}

		const request: IUserIDRequest = {
			UserID: this.username,
		};
		this.userData.userId = this.username;
		this.isForgotPasswordLoading = true;
		UnlockAcountEmail.fetchEmail(request)
			.pipe(finalize(() => (this.isForgotPasswordLoading = false)))
			.subscribe(
				(result) => this.resolveForgotPasswordResponse(result),
				(error) => this.handleError(error)
			);
	}

	public resolveForgotPasswordResponse(response: IFetchUnlockEmailResponse) {
		if (response && response.email.mailAddress != null) {
			this.userData.hasEmail = true;
			UserData.update(this.userData);
			this.modalService.open(PasswordRetrievalComponent, {
				windowClass: 'modal-small',
			});
		} else {
			this.userData.hasEmail = false;
			UserData.update(this.userData);
			let modalRef = this.modalService.open(RestoreWithAuthenticationComponent);
			modalRef.componentInstance.hasBackBtn = false;
			modalRef.componentInstance.isBlocked = this.isBlocked;
		}
	}

	public hasValidFields() {
		if (this.loginStep === 1) {
			if (this.username && this.username.trim().length > 4) return true;
			else return false;
		}
		if (this.loginStep === 2) {
			if (this.password && this.password.length > 4) return true;
			else return false;
		}
	}

	public createNewUser(status: string) {
		let modaleRef = this.modalService.open(CreatePasswordComponent);
		modaleRef.componentInstance.isNewUser = status === 'NEW';
		modaleRef.componentInstance.userCode = this.username;
	}

	public forgotPasswordClicked(): void {
		if (this.isCorporateUser) {
			this.uiShowForgotCorpUser = true;
			return;
		}

		this.retrievePassword();
	}

	private initializeSignInRequest() {
		this.request = {
			username: undefined,
			password: undefined,
			rememberLogin: undefined,
			returnUrl: undefined,
			userID: undefined,
		};
	}
}
